import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Edit, Trash2, Eye, MousePointer, Calendar } from "lucide-react";
import { toast } from "sonner";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

interface Offer {
  id: string;
  title: string;
  description: string;
  offer_type: string;
  image_url: string | null;
  price: number | null;
  discount_percentage: number | null;
  status: string;
  is_active: boolean;
  view_count: number;
  click_count: number;
  created_at: string;
  start_date?: string | null;
  end_date?: string | null;
}

interface OfferCardProps {
  offer: Offer;
  onUpdate: () => void;
  onDelete: () => void;
}

export const OfferCard = ({ offer, onUpdate, onDelete }: OfferCardProps) => {
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      const { error } = await supabase
        .from("partner_offers")
        .delete()
        .eq("id", offer.id);

      if (error) throw error;
      toast.success("Offer deleted successfully");
      onDelete();
    } catch (error) {
      console.error("Error deleting offer:", error);
      toast.error("Failed to delete offer");
    } finally {
      setDeleting(false);
      setShowDeleteDialog(false);
    }
  };

  const handleToggleActive = async () => {
    try {
      const { error } = await supabase
        .from("partner_offers")
        .update({ is_active: !offer.is_active })
        .eq("id", offer.id);

      if (error) throw error;
      toast.success(offer.is_active ? "Offer deactivated" : "Offer activated");
      onUpdate();
    } catch (error) {
      console.error("Error toggling offer:", error);
      toast.error("Failed to update offer");
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "approved":
        return "bg-green-500";
      case "pending":
        return "bg-yellow-500";
      case "rejected":
        return "bg-red-500";
      default:
        return "bg-gray-500";
    }
  };

  const formatPrice = (cents: number | null) => {
    if (!cents) return "Free";
    return `$${(cents / 100).toFixed(2)}`;
  };

  return (
    <>
      <Card className="relative overflow-hidden">
        <div className="absolute top-2 right-2 flex gap-2 z-10">
          <Badge className={getStatusColor(offer.status)}>
            {offer.status}
          </Badge>
          {!offer.is_active && (
            <Badge variant="secondary">Inactive</Badge>
          )}
        </div>

        {offer.image_url && (
          <div className="h-48 overflow-hidden">
            <img
              src={offer.image_url}
              alt={offer.title}
              className="w-full h-full object-cover"
            />
          </div>
        )}

        <CardHeader className={offer.image_url ? "pt-4" : ""}>
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1">
              <CardTitle className="line-clamp-2">{offer.title}</CardTitle>
              <CardDescription className="mt-1">
                <Badge variant="outline" className="capitalize">
                  {offer.offer_type}
                </Badge>
              </CardDescription>
            </div>
          </div>
        </CardHeader>

        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground line-clamp-3">
            {offer.description}
          </p>

          <div className="flex items-center justify-between text-sm">
            <div className="flex gap-4">
              <span className="flex items-center gap-1">
                <Eye className="h-4 w-4" />
                {offer.view_count}
              </span>
              <span className="flex items-center gap-1">
                <MousePointer className="h-4 w-4" />
                {offer.click_count}
              </span>
            </div>
            <div className="font-semibold">
              {formatPrice(offer.price)}
              {offer.discount_percentage && (
                <span className="text-green-600 ml-2">
                  -{offer.discount_percentage}%
                </span>
              )}
            </div>
          </div>

          {(offer.start_date || offer.end_date) && (
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Calendar className="h-3 w-3" />
              {offer.start_date && <span>From {new Date(offer.start_date).toLocaleDateString()}</span>}
              {offer.end_date && <span>To {new Date(offer.end_date).toLocaleDateString()}</span>}
            </div>
          )}

          <div className="flex gap-2 pt-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleToggleActive}
              className="flex-1"
            >
              {offer.is_active ? "Deactivate" : "Activate"}
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => toast.info("Edit functionality coming soon")}
            >
              <Edit className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setShowDeleteDialog(true)}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Offer</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{offer.title}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} disabled={deleting}>
              {deleting ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
